import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToasterService } from '@purespectrum1/ui/toaster-service';

import { environment } from '../../../environments/environment';
import { CompanyService } from '../../shared/services/company/company.service';
import { SupplierSettingService } from '../supplier-setting-service/supplier-setting.service';
import { QuestionsLibraryService } from '../../questions-library/questions-library-services/questions-library-services.service';
import { CompaniesResponse } from '../../shared/interfaces/company.interface';
import { Constants } from './supplier-settings-constants';
import {
  ConfigFields,
  SelectedSupplierSettings,
  SingleSupplier,
  SupplierSettingsResponse,
} from './supplier-settings.interface';
import {
  SegmentParentQuestion,
  UpdateSegmentQuestionPayload,
} from 'src/app/questions-library/questions-library-services/questions-library-services.interface';
@Component({
  selector: 'ps-supplier-settings',
  templateUrl: './supplier-settings.component.html',
  styleUrls: ['./supplier-settings.component.css'],
})
export class SupplierSettingsComponent implements OnInit, OnDestroy {
  public suppliers: SingleSupplier[] = [];
  public selectedSupplier!: SingleSupplier;
  public supplierFields!: ConfigFields[];
  public selectedSupplierConfig!: SelectedSupplierSettings;
  private _subscription: Subscription = new Subscription();
  public microSurveyForm!: UntypedFormGroup;
  public showSegmentSelection: boolean = false;
  public availableSegmentLocales: string[] = [];
  public selectedSegmentParents: SegmentParentQuestion[] = [];
  public showSegmentParents: SegmentParentQuestion[] = [];
  public allSegmentParents: SegmentParentQuestion[] = [];
  public isUpdateLocales: boolean = false;
  public segmentUpdatePayload: UpdateSegmentQuestionPayload =
    this.getDefaultSegmentPayload();
  public selectedSegmentParentId: number | null = null;
  constructor(
    private _companyService: CompanyService,
    private _supplierSettingService: SupplierSettingService,
    private _questionLibraryService: QuestionsLibraryService,
    private _toastrService: ToasterService,
    private _fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this._generateForm();
    this._getSuppliers();
  }

  _getSuppliers() {
    const suppliers$ = this._companyService.getSupplierCompanies().subscribe(
      (response: CompaniesResponse) => {
        this.suppliers = response.companies;
      },
      (error) => {
        this._toastrService.error(error.error.msg);
      }
    );
    this._subscription.add(suppliers$);
  }

  _generateForm() {
    this.microSurveyForm = this._fb.group({
      active: false,
      showMicrosurveySettings: false,
      showConsent: false,
      currencyName: ['', Validators.required],
      conversionValue: [null, Validators.required],
      rewardAmount: [null, Validators.required],
    });
  }

  _buildUpdatePayload(supplierId: number, value: boolean, settingType: string) {
    const mapper: any = {
      supplierType: (_value: boolean) => {
        return {
          supplier_type: _value ? 'private' : 'public',
        };
      },
      dynamicProfiler: (_value: boolean) => {
        return {
          dynamicProfiler: !!_value,
        };
      },
      isPrivacyPolicyActive: (_value: boolean) => {
        return {
          isPrivacyPolicyActive: !!_value,
        };
      },
      isDataQualityQuestionsActive: (_value: boolean) => {
        return {
          isDataQualityQuestionsActive: !!_value,
        };
      },
      isAsrRedirectionEnable: (_value: boolean) => {
        return {
          isAsrRedirectionEnable: !!_value,
        };
      },
      cookieYesIntegration: (_value: boolean) => {
        return {
          cookieYesIntegration: !!_value,
        };
      },
      skipPreScreener: (_value: boolean) => {
        return {
          skipPreScreener: !!_value,
        };
      },
      isScreenerV2: (_value: boolean) => {
        return {
          isScreenerV2: !!_value,
        };
      },
      isSegmentationEnabled: (_value: boolean) => {
        return {
          isSegmentationEnabled: !!_value,
        };
      },
    };
    if (!mapper[settingType]) {
      this._toastrService.error('Something went wrong in mapping!');
    }
    return Object.assign({}, mapper[settingType](value), { cmp: supplierId });
  }
  /**
   * on change of any setting, this function invoked from html and then setting saved in db with service invocation.
   * @param supplierId supplier id - unique id for each supplier
   * @param value each setting has boolean value
   * @param settingType setting flag which used to send with PUT supplier setting api
   */
  changeSupplierSetting(
    supplierId: number,
    value: boolean,
    settingType: string
  ) {
    const payload = this._buildUpdatePayload(supplierId, value, settingType);
    const updateSetting$ = this._supplierSettingService
      .updateSupplierSetting(supplierId, payload)
      .subscribe(
        (response) => {
          this._toastrService.success(response.msg);
        },
        (error) => {
          this._toastrService.error(error.error.msg);
        }
      );
    this._subscription.add(updateSetting$);
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  selectSupplier(supplier: SingleSupplier) {
    this.showSegmentSelection = false;
    this.resetSegmentConfigState();
    this._getSupplierSettings(supplier.id);
    this.selectedSupplier = supplier;
    if (environment.ZAPPI_SUPPLIERS_WITH_SEGMENT_ACCESS.includes(supplier.id)) {
      this.getSupplierSegmentQuestions();
    }
  }

  _getSupplierSettings(supplierId: number) {
    this.supplierFields = [];
    this._supplierSettingService.getSupplierSettings(supplierId).subscribe(
      (response: SupplierSettingsResponse) => {
        if (response.supplier && response.supplier.length) {
          this.selectedSupplierConfig = {
            ...response.supplier[0],
            supplierType:
              response.supplier[0].supplier_type === 'public' ? false : true,
          };
          if (response.supplier[0]?.microSurveySetting) {
            this.microSurveyForm?.patchValue(
              response.supplier[0].microSurveySetting
            );
          } else {
            this.microSurveyForm.reset({
              active: false,
              showMicrosurveySettings: false,
              showConsent: false,
              currencyName: '',
              conversionValue: null,
              rewardAmount: null,
            });
          }
          this.supplierFields = this.getSupplierFields(supplierId);
        }
      },
      (error: any) => {
        this._toastrService.error(error.error.msg);
      }
    );
  }

  getSupplierFields(supplierId: number) {
    if (environment.ZAPPI_SUPPLIERS_WITH_SEGMENT_ACCESS.includes(supplierId)) {
      return Constants.SUPPLIER_FIELDS;
    }
    return Constants.SUPPLIER_FIELDS.filter(
      (field) => field.section !== 'SEGMENTATION CONFIG'
    );
  }
  enableSegmentConfig(value: boolean = false) {
    if (value) {
      this.showSegmentSelection = true;
      return;
    }
    this.showSegmentSelection = false;
  }

  selectSegmentParent(event: any) {
    if (!event) {
      this.availableSegmentLocales = [];
      return;
    }
    const existingLocales = this.selectedSegmentParents.find(
      (p) => p.id === event.id
    );
    this.availableSegmentLocales = !existingLocales
      ? event.locales
      : event.locales.filter(
          (loc: string) =>
            !existingLocales.locales.some((el: string) => el === loc)
        );
  }

  removeSegmentLocale(locale: string, segment: SegmentParentQuestion) {
    const isLastLocale = !!(locale && segment.locales.length === 0);
    let questionPayload = {
      locales: [locale],
      id: segment.id,
      name: segment.name,
    };
    this.updateSupplierToSegmentation('remove', questionPayload, !isLastLocale);
  }

  customSearch(term: string, item: any) {
    term = term.toLowerCase();
    return (
      item.name.toLowerCase().indexOf(term) > -1 ||
      item.id.toString().indexOf(term) > -1
    );
  }

  getSupplierSegmentQuestions() {
    const supId = this.selectedSupplier.id;
    this._questionLibraryService.getSupplierSegmentQuestions(supId).subscribe(
      (response) => {
        if (response) {
          if (response.selectedSegmentParents.length) {
            this.enableSegmentConfig(true);
          }
          this.selectedSegmentParents = response.selectedSegmentParents || [];
          this.allSegmentParents = response.segmentParent;
          this.showSegmentParents = response.segmentParent;
          this.refreshAvailableParents();
        }
      },
      (error) => {
        this._toastrService.error('Error fetching segmentation questions');
      }
    );
  }

  refreshAvailableParents() {
    this.showSegmentParents = this.allSegmentParents.filter((parent) => {
      const selected = this.selectedSegmentParents.find(
        (s) => s.id === parent.id
      );
      if (!selected) {
        return true;
      }
      return selected.locales.length < parent.locales.length;
    });
  }

  getDefaultSegmentPayload() {
    return {
      path: '',
      supId: 0,
      locales: [],
      action: '',
    };
  }

  updateSupplierToSegmentation(
    action: string,
    question: SegmentParentQuestion | null = null,
    isLocaleUpdate: boolean = false
  ) {
    this.segmentUpdatePayload.path = '/accessibleSupplierIds';
    this.segmentUpdatePayload.action = action;
    this.segmentUpdatePayload.supId = this.selectedSupplier?.id;
    if (action === 'remove' && question) {
      this.segmentUpdatePayload.locales = question.locales;
      this.selectedSegmentParentId = question.id;
    }
    if (!this.selectedSegmentParentId) {
      this._toastrService.error('Please select A Segmentation Parent');
      return;
    }
    if (
      !this.segmentUpdatePayload.locales ||
      this.segmentUpdatePayload.locales.length === 0
    ) {
      this._toastrService.error('Please select at least one locale');
      return;
    }
    this._questionLibraryService
      .updateSegmentQuestion(
        this.selectedSegmentParentId,
        this.segmentUpdatePayload
      )
      .subscribe(
        (response) => {
          if (!response) {
            return;
          }
          if (action === 'add') {
            this.handleUpsertSupplier(response);
          } else {
            this.handleRemoveSupplier(response, isLocaleUpdate);
          }
          this.resetSegmentConfigState();
          this.refreshAvailableParents();
        },
        (error) => {
          this._toastrService.error('Error adding supplier to segmentation');
        }
      );
  }

  handleUpsertSupplier(response: SegmentParentQuestion) {
    const existingIndex = this.selectedSegmentParents.findIndex(
      (q) => q.id === response.id
    );
    if (existingIndex > -1) {
      const mergedLocales = [
        ...this.selectedSegmentParents[existingIndex].locales,
        ...response.locales,
      ];
      this.selectedSegmentParents[existingIndex] = {
        ...this.selectedSegmentParents[existingIndex],
        locales: [...new Set(mergedLocales)],
      };
      this._toastrService.success('Locales updated successfully');
    } else {
      this.selectedSegmentParents.push(response);
      this._toastrService.success('Supplier added successfully');
    }
  }

  handleRemoveSupplier(
    removedData: SegmentParentQuestion,
    isLocaleUpdate: boolean
  ) {
    if (isLocaleUpdate) {
      this.selectedSegmentParents = this.selectedSegmentParents.filter(
        (p) => p.locales.length > 0
      );
    } else {
      this.selectedSegmentParents = this.selectedSegmentParents.filter(
        (q) => q.id !== removedData.id
      );
    }
  }

  resetSegmentConfigState() {
    this.selectedSegmentParentId = null;
    this.segmentUpdatePayload = this.getDefaultSegmentPayload();
  }

  updateSupplierSettings(value: boolean, config: string, settingType: string) {
    if (settingType !== 'MICROSURVEY_SETTINGS') {
      return this.changeSupplierSetting(
        this.selectedSupplier.id,
        value,
        config
      );
    }
    this.microSurveyForm?.patchValue({ [config]: value });
  }

  showInput(value: string) {
    const inputRequiredFields = [
      'currencyName',
      'conversionValue',
      'rewardAmount',
    ];
    return inputRequiredFields.includes(value);
  }

  saveMicroSurveySettings() {
    if (this.microSurveyForm?.invalid) {
      this._toastrService.error('Please fill all the required fields');
      return;
    }
    const microSurveySetting = this.microSurveyForm?.value;
    microSurveySetting.conversionValue = parseFloat(
      microSurveySetting.conversionValue
    );
    microSurveySetting.rewardAmount = parseFloat(
      microSurveySetting.rewardAmount
    );
    const payload = {
      cmp: this.selectedSupplier.id,
      microSurveySetting: microSurveySetting,
    };
    const updateSetting$ = this._supplierSettingService
      .updateSupplierSetting(this.selectedSupplier.id, payload)
      .subscribe(
        (response) => {
          this._toastrService.success(response.msg);
        },
        (error) => {
          this._toastrService.error(error.error.msg);
        }
      );
    this._subscription.add(updateSetting$);
  }
}
